'use client';

import '@mantine/dates/styles.css';
import { type ReactNode, useState, useEffect, useCallback } from 'react';
import { AppShell } from '@mantine/core';
import AppMain from '../../components/app-main/AppMain';
import { useMediaQuery } from '@mantine/hooks';
import SidebarV2 from '@components/sidebar/sidebar-v2';
import { useSidebar } from '../../contexts/sidebar-context';
import { useTheme } from '@contexts/ThemeContext';

type Props = {
  children: ReactNode;
};

const SIDEBAR_MINIMIZED_KEY = 'jarvis-sidebar-minimized';

// SSR-safe lazy init — read viewport once on mount so mobile never flashes
// the open drawer. Falls back to "open" on the server / when matchMedia is
// unavailable so desktop renders unchanged.
const getInitialDrawerOpen = () => {
  if (typeof window === 'undefined') return true;
  return !window.matchMedia('(max-width: 991px)').matches;
};

const getInitialMinimized = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(SIDEBAR_MINIMIZED_KEY) === 'true';
};

export default function DashboardContent({ children }: Props) {
  const { colors: themeColors } = useTheme();
  // Match Mantine AppShell `md` breakpoint (~992px) so drawer vs permanent
  // sidebar stay in sync — avoids reserved navbar width + hamburger together.
  const tablet_match = useMediaQuery('(max-width: 991px)');
  const isDesktop = tablet_match === false;

  const [isDrawerOpen, setIsDrawerOpen] = useState(getInitialDrawerOpen);
  const [isDrawerMinimized, setIsDrawerMinimized] = useState(getInitialMinimized);
  const { isVisible } = useSidebar();

  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);
  const toggleMinimize = useCallback(() => {
    setIsDrawerMinimized((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_MINIMIZED_KEY, String(next));
      return next;
    });
  }, []);

  // Update mobile drawer state based on screen size
  useEffect(() => {
    if (tablet_match) {
      setIsDrawerOpen(false);
    } else {
      setIsDrawerOpen(true);
    }
  }, [tablet_match]);

  // ⌘B / Ctrl+B toggles sidebar collapse on desktop
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
        if (!isDesktop || !isVisible) return;
        e.preventDefault();
        toggleMinimize();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isDesktop, isVisible, toggleMinimize]);

  // AppShell turns numeric widths into rem, and rem already follows the text
  // size (html font-size), so 240 / 64 grow by themselves. Multiplying here
  // too would scale the rail twice.
  const desktopWidth = isDrawerMinimized ? 64 : 240;
  // 17.5rem = 280px at 100%, never wider than most of a phone screen. calc()
  // keeps Mantine's rem converter from splitting the min() on its comma.
  const mobileWidth = 'calc(min(17.5rem, 85vw))';

  return (
    <AppShell
      layout="alt"
      footer={{ height: 0, collapsed: true }}
      navbar={{
        // Mobile: overlay drawer (AppShell collapses navbar off-canvas).
        // Desktop: permanent rail toggles between 240 / 64.
        width: { base: mobileWidth, md: desktopWidth },
        breakpoint: 'md',
        collapsed: { mobile: !isDrawerOpen, desktop: false },
      }}
      padding={0}
    >
      <AppShell.Navbar
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          padding: 0,
          overflow: 'hidden',
          // Keep every wheel/touch gesture over the sidebar inside the sidebar:
          // without this, scrolling past the end of the nav list (or over the
          // non-scrolling header/footer) chains into the page behind it.
          overscrollBehavior: 'contain',
          zIndex: 100,
          display: isVisible ? 'block' : 'none',
        }}
      >
        <SidebarV2
          isOpen={isDrawerOpen}
          isMinimized={Boolean(isDesktop && isDrawerMinimized)}
          toggleDrawer={toggleDrawer}
          toggleMinimize={toggleMinimize}
          isVisible={isVisible}
        />
      </AppShell.Navbar>
      <AppShell.Main
        style={{
          transition: 'padding-left 300ms ease-in-out',
          backgroundColor: themeColors.background,
          backgroundImage: themeColors.backgroundGradient,
          backgroundAttachment: themeColors.backgroundGradient ? 'fixed' : undefined,
          paddingLeft: isVisible ? undefined : 0,
          paddingTop: 0,
        }}
      >
        {/* The drawer toggle lives inside the header rather than floating over
            it — as a fixed overlay it sat under the header strip and its taps
            went to the header instead. Mobile/tablet only, drawer closed. */}
        <AppMain
          showMenuButton={isVisible && !isDesktop && !isDrawerOpen}
          onMenuClick={toggleDrawer}
        >
          {children}
        </AppMain>
      </AppShell.Main>
      <AppShell.Footer style={{ height: 0 }} />
    </AppShell>
  );
}
